import { Component, inject, OnInit } from '@angular/core';
import {
  combineLatest,
  debounceTime,
  from,
  Observable,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { AbstractComponent } from './shared/components/abstract.component';
import { AppStateService } from './shared/services/app-state.service';
import { reattach } from './shared/utils/rxjs.utils';
import { Metadata } from './shared/models/sys/metadata';
import { SeoService } from './shared/services/html/seo.service';
import { TrackingService } from './shared/services/html/tracking.service';
import { ResponsiveService } from './shared/services/html/responsive.service';
import { BootstrapHttpService } from './shared/services/http/bootstrap-http.service';
import { BootstrapDto } from './shared/models/dto/bootstrap-dto';
import { SnackbarService } from './shared/services/snackbar.service';
import { HomeHttpService } from './shared/services/http/home-http.service';
import { HomeDto } from './shared/models/dto/home-dto';
import { AppDialogService } from './shared/services/app-dialog.service';
import { AppRouterService } from './shared/services/app-router.service';
import { RegionDto } from './shared/models/dto/region-dto';
import { ConfigService } from './shared/services/config.service';
import { UiTourService } from './shared/services/ui-tour.service';
import {
  ConfirmDialogComponent,
  ConfirmOptions,
} from './shared/components/dialogs/confirm-dialog/confirm-dialog.component';
import { Routes } from './shared/constants/routes/routes.constant';
import { Translations } from './shared/constants/translations';
import { TranslateService } from './shared/services/translations/translate.service';
import { UiTourComponent } from './shared/components/ui/ui-tour/ui-tour.component';
import { MenuComponent } from './shared/components/ui/menu/menu.component';
import { LoaderComponent } from './shared/components/ui/loader/loader.component';
import { NgClass } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { SnackbarComponent } from './shared/components/ui/snackbar/snackbar.component';
import { UiTourDirective } from './shared/directives/ui-tour.directive';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    UiTourComponent,
    MenuComponent,
    LoaderComponent,
    NgClass,
    RouterOutlet,
    SnackbarComponent,
    UiTourDirective,
  ],
})
export class AppComponent extends AbstractComponent implements OnInit {
  private readonly _appState: AppStateService = inject(AppStateService);
  private readonly _config: ConfigService = inject(ConfigService);
  private readonly _bootstrap: BootstrapHttpService =
    inject(BootstrapHttpService);
  private readonly _home: HomeHttpService = inject(HomeHttpService);
  private readonly _snack: SnackbarService = inject(SnackbarService);
  private readonly _seo: SeoService = inject(SeoService);
  private readonly _tracking: TrackingService = inject(TrackingService);
  private readonly _responsive: ResponsiveService = inject(ResponsiveService);
  private readonly _dialog: AppDialogService = inject(AppDialogService);
  private readonly _router: AppRouterService = inject(AppRouterService);
  private readonly _uiTour: UiTourService = inject(UiTourService);
  private readonly _translate: TranslateService = inject(TranslateService);

  public loading: boolean = false;
  public ready: boolean = false;

  public ngOnInit(): void {
    // Bootstrap configuration

    const boot: Observable<unknown> = this._config.bootstrap().pipe(
      switchMap(() => from(this._translate.bootstrap())),
      tap(() => {
        this.ready = true;
      })
    );

    boot.subscribe(() => {
      this._responsive.bootstrap();
      this._appState.loading$
        .pipe(reattach(), debounceTime(10), takeUntil(this._destroyed$))
        .subscribe((queue: number) => {
          this.loading = !!queue;
        });

      this._appState.metadata$
        .pipe(takeUntil(this._destroyed$))
        .subscribe((metadata?: Metadata) => {
          this._seo.title = metadata?.title;
          this._seo.description = metadata?.description;
          this._seo.keywords = metadata?.keywords;
          this._seo.author = metadata?.author;
          this._seo.image = metadata?.image;
          this._seo.robots = metadata?.robots;
          this._seo.setCanonicalURL(metadata?.canonical);

          if (metadata) {
            this._tracking.trackPage();
          }
        });

      this._seo.bootstrap();
      this._tracking.bootstrap();

      combineLatest([
        this._bootstrap.bootstrap(),
        this._home.getFigures(),
      ]).subscribe(
        ([bootstrap, figures]: [BootstrapDto | null, HomeDto | null]) => {
          if (!bootstrap) {
            this._snack.error(this._translate.instant('error.network'));
            return;
          }
          bootstrap.regions.sort((a: RegionDto, b: RegionDto) =>
            a.label.localeCompare(b.label)
          );
          bootstrap.departments.sort((a: RegionDto, b: RegionDto) =>
            a.label.localeCompare(b.label)
          );
          this._appState.globalFilters = bootstrap;
          this._appState.homeFigures = figures ?? undefined;

          // Launch guided tour
          this.launchGuidedTour();
        }
      );
    });
  }

  private launchGuidedTour(): void {
    if (!this._appState.hasTakenTour) {
      setTimeout(() => {
        this._dialog
          .openPopin<boolean, ConfirmOptions, ConfirmDialogComponent>(
            ConfirmDialogComponent,
            {
              title: this._translate.instant(Translations.TourTitle),
              content: this._translate.instant(Translations.TourContent),
              validate: this._translate.instant(Translations.Yes),
              cancel: this._translate.instant(Translations.No),
            }
          )
          .closed.pipe(takeUntil(this.destroyed$))
          .subscribe((confirm: boolean | undefined) => {
            this._appState.hasTakenTour = true;
            if (confirm) {
              this._router.navigate(Routes.home);
              this._uiTour.startTour();
            }
          });
      }, 1000);
    }
  }
}
